/**
 * Hand-written types for the communications template admin API (US-6.5).
 *
 * Per CLAUDE.md TD-07 the Orval-generated client is unusable at call sites,
 * so every hook calls `httpClient<T>` directly with these hand-written
 * shapes. They mirror the API source 1:1 — keep them in sync when the
 * controllers change:
 *
 *   wanderfit-api/src/communications/engine/admin/communication-admin.repository.ts
 *     → AdminCommunication, AdminChannel, AdminTranslation, CommunicationLocale
 *   wanderfit-api/src/communications/engine/admin/admin-communications.input.ts
 *     → request body contracts + length limits
 *
 * The live preview renders client-side (see `translations/render-template.ts`),
 * mirroring the server's `HandlebarsTemplateRenderer` (`noEscape: true`).
 *
 * All collection types are `readonly` per the project's immutability rule.
 * Date fields arrive as ISO strings (JSON-serialised `Date`).
 */

/** The four delivery channels a communication can carry. */
export const NOTIFICATION_CHANNELS = ["EMAIL", "SMS", "PUSH", "WHATSAPP"] as const;

export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/**
 * Stable display order for channels. The engine's cascade order is
 * SMS → WhatsApp → Push with an EMAIL floor, but for the admin UI we lead
 * with EMAIL (the always-on floor) so the most-important copy is first.
 */
export const CHANNEL_DISPLAY_ORDER: readonly NotificationChannel[] = [
  "EMAIL",
  "SMS",
  "WHATSAPP",
  "PUSH",
] as const;

/**
 * Channels whose translations carry a `subject` (email subject line / push
 * notification title). SMS + WhatsApp are body-only — their subject is
 * always `null`, and the API rejects a non-null subject for them.
 */
export const CHANNELS_WITH_SUBJECT: readonly NotificationChannel[] = ["EMAIL", "PUSH"] as const;

export function channelHasSubject(channel: NotificationChannel): boolean {
  return CHANNELS_WITH_SUBJECT.includes(channel);
}

/** Who a communication targets. `ADMIN` ⇒ email-only, preferences skipped. */
export type CommunicationAudience = "USER" | "ADMIN";

export interface AdminTranslation {
  readonly id: string;
  readonly localeCode: string;
  readonly version: number;
  readonly subject: string | null;
  readonly body: string;
  readonly isActive: boolean;
}

export interface AdminChannel {
  readonly id: string;
  readonly channel: NotificationChannel;
  readonly isActive: boolean;
  readonly translations: readonly AdminTranslation[];
}

export interface AdminCommunication {
  readonly id: string;
  readonly key: string;
  readonly description: string | null;
  readonly audience: CommunicationAudience;
  readonly isCritical: boolean;
  readonly emailBackup: boolean;
  readonly channels: readonly AdminChannel[];
}

/** Engine-owned locale catalog (`communication_locales`). */
export interface CommunicationLocale {
  readonly code: string;
  readonly language: string;
  readonly isDefault: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** A single template variable a communication's copy can interpolate. Mirrors
 *  the API's `CommunicationVariableModel` (name + always-true `required`). */
export interface CommunicationVariable {
  readonly name: string;
  readonly required?: boolean;
}

/** Response of `GET /admin/communications/:id/variables` (API-COMM-4). */
export interface CommunicationVariables {
  readonly variables: readonly CommunicationVariable[];
  /** Ready-to-send sample object the preview interpolates the {{vars}} with. */
  readonly sampleData: Readonly<Record<string, unknown>>;
}

// ── Request inputs ────────────────────────────────────────────────

/** Edit copy → creates a new active version. `subject: null` clears it. */
export interface UpdateTranslationInput {
  readonly subject?: string | null;
  readonly body?: string;
}

/** Create the first translation for a `(channel, locale)` (API-COMM-2). */
export interface CreateTranslationInput {
  readonly localeCode: string;
  readonly subject?: string | null;
  readonly body: string;
}

/** Edit communication-level config (API-COMM-3). */
export interface UpdateConfigInput {
  readonly description?: string | null;
  readonly audience?: CommunicationAudience;
  readonly isCritical?: boolean;
  readonly emailBackup?: boolean;
}

/** Toggle a channel on/off (API-COMM-3). */
export interface ToggleChannelInput {
  readonly isActive: boolean;
}

/**
 * Create a communications locale (API-COMM-1). Only `code` is accepted — the
 * API derives `language` server-side from the code's primary subtag to keep the
 * same-language fallback consistent. Named to match the API's
 * `CreateCommunicationLocaleInput` DTO (the generic `CreateLocaleInput` Swagger
 * name belongs to the TOS module — a different feature).
 */
export interface CreateCommunicationLocaleInput {
  readonly code: string;
}

/**
 * Add a channel TYPE to a communication (BO-4.2). The channel-type enum is
 * fixed; this creates the per-communication row (or revives a soft-deleted one).
 */
export interface CreateChannelInput {
  readonly channel: NotificationChannel;
}

/**
 * Communications locale-code validation, mirroring the API's `Matches`
 * regex exactly (`admin-communications.input.ts` `LOCALE_CODE_RE`): a 2–3
 * letter language subtag + optional script/region/variant subtags. Wider than
 * the TOS `bcp47.ts` validator (which only allows a 2-letter region), so it
 * accepts `es-419`, `zh-Hant`, `zh-Hant-TW`. Kept local to the slice so the
 * BO never blocks a code the communications API would accept.
 */
const COMMUNICATION_LOCALE_CODE_RE = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;

export function isValidCommunicationLocaleCode(code: string): boolean {
  return code.length <= LOCALE_CODE_MAX && COMMUNICATION_LOCALE_CODE_RE.test(code);
}

/**
 * Outcome of a test-dispatch (API-COMM-5). Mirrors the API's
 * `TestDispatchResultModel.status` enum. Every value is returned with HTTP
 * `200` — the `SKIPPED_*` / `FAILED` states describe why nothing was delivered
 * (they are NOT transport errors).
 */
export type TestDispatchStatus = "SENT" | "SKIPPED_SUPPRESSED" | "SKIPPED_PROVIDER_OFF" | "FAILED";

export const TEST_DISPATCH_STATUSES: readonly TestDispatchStatus[] = [
  "SENT",
  "SKIPPED_SUPPRESSED",
  "SKIPPED_PROVIDER_OFF",
  "FAILED",
] as const;

/**
 * Fire an existing catalog communication to a real user over a forced channel
 * (API-COMM-5). The channel bypasses the cascade + user preferences; it must be
 * an ACTIVE channel on the communication. `data` must match the template's
 * tokens exactly — the API validates and fails the dispatch otherwise.
 */
export interface TestDispatchInput {
  readonly recipient: string;
  readonly channel: NotificationChannel;
  readonly locale?: string;
  readonly data?: Readonly<Record<string, unknown>>;
}

/** Response of `POST /admin/communications/:id/test-dispatch` (API-COMM-5). */
export interface TestDispatchResult {
  readonly channel: NotificationChannel;
  readonly status: TestDispatchStatus;
  readonly providerMessageId: string | null;
  readonly errorMessage: string | null;
}

// ── Backend body-length contracts (mirror the API DTOs) ───────────

/** Max length of the test-dispatch recipient email (API DTO `maxLength`). */
export const RECIPIENT_MAX = 320;
export const SUBJECT_MAX = 500;
export const BODY_MAX = 20_000;
export const KEY_QUERY_MAX = 200;
export const LOCALE_QUERY_MAX = 35;
export const LOCALE_CODE_MAX = 35;
